import React, { useEffect, useState } from "react";
import { FileText, Film, Image as ImageIcon, Music, X } from "lucide-react";
import { filterMediaByLanguage } from "@shared/lib/media";

// F-F visitor media gallery: a language-filtered grid of supporting media
// (image + audio + video + pdf). Defaults the filter to the visitor's active
// language
// with an "All" option (per the F-F UX flow). Audio/video cards are native
// <audio>/<video> elements, so the page-level single-playback coordinator
// (capture-phase "play" listener in ExhibitDetail) pauses other tracks +
// narration automatically; pdf cards are a simple open/download link (no
// inline player).
//
// camelCase wire fields per ADR-0020 (item.assetId, item.kind, item.language).

const FILTERS = ["all", "en", "tc", "sc"];
const FILTER_LABEL = { all: "All", en: "EN", tc: "TC", sc: "SC" };
const GALLERY_KINDS = ["audio", "video", "pdf", "image"];

// Mobile: play a tapped video fullscreen rather than as a small inline sector.
// iOS uses webkitEnterFullscreen on the <video> itself; everyone else uses the
// standard element Fullscreen API. Failures degrade silently to inline.
function enterFullscreen(el) {
  const fn =
    el.requestFullscreen ||
    el.webkitRequestFullscreen ||
    el.webkitEnterFullscreen ||
    el.msRequestFullscreen;
  if (!fn) return;
  try {
    const maybePromise = fn.call(el);
    if (maybePromise && typeof maybePromise.catch === "function") {
      maybePromise.catch(() => {});
    }
  } catch {
    /* fullscreen not permitted — keep playing inline */
  }
}

const MediaGallery = ({ media, api, language, t }) => {
  const items = (media || []).filter((m) => GALLERY_KINDS.includes(m.kind));
  const [filter, setFilter] = useState(language || "all");
  // Tapped image shown full-screen in an in-app lightbox; null = closed. An
  // overlay (not the native Fullscreen API) so the ✕ / tap-to-dismiss exit is
  // discoverable and identical on every phone.
  const [lightbox, setLightbox] = useState(null);

  // While the lightbox is open: Esc closes it (desktop) and the page behind
  // stops scrolling. Both are torn down on close.
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightbox]);

  // Nothing to show → render nothing (don't clutter the page with an empty
  // section). The filter only appears once there is at least one item.
  if (items.length === 0) return null;

  const shown = filterMediaByLanguage(items, filter);

  return (
    <div
      className="space-y-4 pt-6 mt-6 border-t border-white/10"
      data-testid="media-gallery"
    >
      <h2 className="text-2xl font-heading font-bold text-white">
        {t({ en: "Media", tc: "媒體", sc: "媒体" })}
      </h2>

      {/* Language filter — clearly part of the Media section (below its
          heading), with dark-theme-visible buttons. */}
      <div
        className="flex items-center flex-wrap gap-2"
        data-testid="media-filter"
      >
        <span className="text-sm text-white/60 mr-1">
          {t({ en: "Language", tc: "語言", sc: "语言" })}:
        </span>
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-sm border transition-colors ${
              filter === f
                ? "bg-white text-black border-white font-medium"
                : "bg-transparent text-white/80 border-white/30 hover:bg-white/10"
            }`}
            data-testid={`media-filter-${f}`}
          >
            {FILTER_LABEL[f]}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <p className="text-white/60 text-sm" data-testid="media-empty">
          {t({
            en: "No media for this language.",
            tc: "此語言沒有媒體。",
            sc: "此语言没有媒体。",
          })}
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {shown.map((item) => {
            const isVideo = item.kind === "video";
            const isPdf = item.kind === "pdf";
            const isImage = item.kind === "image";
            const Icon = isVideo
              ? Film
              : isPdf
                ? FileText
                : isImage
                  ? ImageIcon
                  : Music;
            const fallbackLabel = isVideo
              ? t({ en: "Video", tc: "影片", sc: "视频" })
              : isPdf
                ? t({ en: "Document", tc: "文件", sc: "文件" })
                : isImage
                  ? t({ en: "Image", tc: "圖片", sc: "图片" })
                  : t({ en: "Audio", tc: "音訊", sc: "音频" });
            const src = `${api}/assets/${item.assetId}`;
            return (
              <div
                key={item.assetId}
                className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3"
                data-testid={`media-${item.kind}-${item.assetId}`}
              >
                <div className="flex items-center gap-2 text-white">
                  <Icon className="w-5 h-5 text-white/70 shrink-0" />
                  <span className="text-sm font-medium truncate">
                    {item.caption || fallbackLabel}
                  </span>
                  <span className="ml-auto text-[10px] uppercase text-white/50 shrink-0">
                    {item.language || "all"}
                  </span>
                </div>
                {isImage ? (
                  <button
                    type="button"
                    onClick={() =>
                      setLightbox({
                        src,
                        alt: item.caption || fallbackLabel,
                      })
                    }
                    className="block w-full cursor-zoom-in"
                    aria-label={t({
                      en: "Enlarge image",
                      tc: "放大圖片",
                      sc: "放大图片",
                    })}
                    data-testid={`media-${item.kind}-enlarge-${item.assetId}`}
                  >
                    <img
                      src={src}
                      alt={item.caption || fallbackLabel}
                      loading="lazy"
                      className="w-full rounded-lg"
                      data-testid={`media-${item.kind}-img-${item.assetId}`}
                    />
                  </button>
                ) : isPdf ? (
                  <a
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full rounded-lg bg-white text-black text-sm font-medium py-2.5 hover:bg-white/90 transition-colors"
                    data-testid={`media-${item.kind}-open-${item.assetId}`}
                  >
                    <FileText className="w-4 h-4" />
                    {t({ en: "Open PDF", tc: "開啟 PDF", sc: "打开 PDF" })}
                  </a>
                ) : isVideo ? (
                  <video
                    controls
                    playsInline
                    preload="metadata"
                    src={src}
                    onPlay={(e) => enterFullscreen(e.currentTarget)}
                    className="w-full rounded-lg"
                    data-testid={`media-${item.kind}-player-${item.assetId}`}
                  />
                ) : (
                  <audio
                    controls
                    preload="none"
                    src={src}
                    className="w-full"
                    data-testid={`media-${item.kind}-player-${item.assetId}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox: tapped photo full-screen. Tap the backdrop or ✕ (or Esc on
          desktop) to shrink back — an explicit, always-visible exit rather than
          relying on the browser's inconsistent fullscreen chrome on phones. */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          role="dialog"
          aria-modal="true"
          data-testid="media-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 rounded-full bg-white/10 p-2 text-white/90 hover:bg-white/20 hover:text-white"
            aria-label={t({ en: "Close", tc: "關閉", sc: "关闭" })}
            data-testid="media-lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            className="max-h-full max-w-full rounded-lg object-contain"
            data-testid="media-lightbox-img"
          />
        </div>
      )}
    </div>
  );
};

export default MediaGallery;
